import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SyncParams {
  brandId: string;
  eventId: string;
  action: "create" | "update";
}

async function extractErrorMsg(error: unknown): Promise<string> {
  if (error instanceof FunctionsHttpError && error.context) {
    try {
      const body = (await error.context.json()) as { error?: string; details?: string };
      if (body?.error && body?.details) return `${body.error}: ${body.details}`;
      if (body?.error) return body.error;
      if (body?.details) return body.details;
    } catch {
      /* response not JSON or already consumed */
    }
  }
  return error instanceof Error ? error.message : "Failed to sync to Eventbrite";
}

export function useEventbriteSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ brandId, eventId, action }: SyncParams) => {
      const { data, error } = await supabase.functions.invoke("create-eventbrite-event", {
        body: { brandId, eventId, action },
      });
      if (error) {
        throw new Error(await extractErrorMsg(error));
      }
      if (!data?.success && (data as { error?: string })?.error) {
        const d = data as { error?: string; details?: string };
        throw new Error(d.details ? `${d.error}: ${d.details}` : (d.error ?? "Failed to sync to Eventbrite"));
      }
      return data;
    },
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: ["event", eventId] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
      toast.success("Event synced to Eventbrite");
    },
    onError: (e: Error) => {
      toast.error(e.message || "Failed to sync to Eventbrite");
    },
  });
}
